import React, { useState, useRef, forwardRef, useEffect, useCallback, useMemo, useImperativeHandle } from 'react';
import Select, { components } from 'react-select';
import { FixedSizeList as List } from 'react-window';
import {
   Box,
   FormControl,
   FormLabel,
   FormErrorMessage,
   Text,
   Button,
   Icon,
   useColorModeValue,
   useDisclosure,
} from '@chakra-ui/react';
import { FiEdit } from 'react-icons/fi';
import { useUser } from 'contexts';
import { useGlobal } from '../../contexts';
import ItemMasterForm from 'views/admin/masters/Pages/ItemMaster/ItemMasterForm';
import CityMasterForm from 'views/admin/masters/Pages/CityMaster/CityMasterForm';
import StateMasterForm from 'views/admin/masters/Pages/StateMaster/StateMasterForm';
import CountryMasterForm from 'views/admin/masters/Pages/CountryMaster/CountryMasterForm';
import TransportMasterForm from 'views/admin/masters/Pages/TransportMaster/TransportMasterForm';
import BankAccountsForm from 'views/admin/masters/Pages/BankAccounts/BankAccountsForm';
import LedgerMasterForm from 'views/admin/masters/Pages/LedgerMaster/LedgerMasterForm';
import SalesRepresentativeMasterForm from 'views/admin/masters/Pages/SalesRepresentativeMaster/SalesRepresentativeMasterForm';
import BrandMasterForm from 'views/admin/masters/Pages/BrandMaster/BrandMasterForm';
import HsnMasterForm from 'views/admin/masters/Pages/HsnMaster/HsnMasterForm';
import AddEditForm from 'views/admin/masters/Pages/AllAccountMaster/AddEditFormForAccount';
import WarehouseMasterForm from 'views/admin/masters/Pages/WarehouseMaster/WarehouseMasterForm';
import ItemGroupMasterForm from 'views/admin/masters/Pages/ItemGroupMaster/ItemGroupMasterForm';
import DaybookForm from 'views/admin/masters/Pages/DaybookMaster/DaybookForm';
import SeriesMasterForm from 'views/admin/masters/Pages/SeriesMaster/SeriesMasterForm';
import ColorMasterForm from 'views/admin/masters/Pages/ColorMaster/ColorMasterForm';
import VehicleMasterForm from 'views/admin/masters/Pages/VehicleMaster/VehicleMasterForm';
import AddUserModal from 'components/navbar/ManageUsers/ManageUserForm';
// Import all your form components here...

const OPTION_HEIGHT = 40;
const MENU_MAX_HEIGHT = 300;

const DynamicDropdown = forwardRef(
   (
      {
         name,
         errorName,
         label,
         openfrom = null,
         showCustomNoOptions = false,
         isMulti = false,
         options = [],
         value,
         onKeyDown,
         errors = {},
         textColor = 'gray.700',
         isRequired = false,
         placeholder = 'Select an option',
         id,
         onChange,
         newoption,
         isInvalid,
         ...props
      },
      ref,
   ) => {
      const [isFocused, setIsFocused] = useState(false);
      const [inputValue, setInputValue] = useState('');
      const [demoname, setDemoname] = useState('');
      const [addoption, setAddoption] = useState();
      const [isEditMode, setIsEditMode] = useState(false);
      const [currentRow, setCurrentRow] = useState({});
      const { onClose } = useDisclosure();
      const { user } = useUser();
      const { companyList } = useGlobal();
      const [isOpen, setIsOpen] = useState(false);
      const [menuIsOpen, setMenuIsOpen] = useState(false);
      const escPressedRef = useRef(false);
      const selectRef = useRef(null);
      const containerRef = useRef(null);
      const isFloating = isFocused || (value !== null && value !== undefined && value !== '');
      const allOptionValues = useMemo(() => options.map((opt) => opt.id), [options]);
      const isAllSelected = isMulti && value?.length === allOptionValues.length;
      const fontcolor = useColorModeValue('#9E9E9E', '#ffffff');
      const selectAllOption = useMemo(
         () => ({
            value: '*',
            label: isAllSelected ? 'Deselect All' : 'Select All',
         }),
         [isAllSelected],
      );

      const mappedOptions = useMemo(() => {
         if (!Array.isArray(options)) return [];

         return isMulti
            ? [
               selectAllOption,
               ...options.map((opt) => ({
                  value: opt.id,
                  label: opt.label ?? opt.name,
               })),
            ]
            : options.map((opt) => ({
               ...opt,
               value: opt.id,
               label: opt.label ?? opt.name,
            }));
      }, [options, isMulti, selectAllOption]);

      const selectedOption = useMemo(() => {
         if (isMulti) {
            if (value?.length === allOptionValues.length) {
               return [
                  selectAllOption,
                  ...mappedOptions.filter((opt) => value.includes(opt.value) && opt.value !== '*'),
               ];
            }
            return mappedOptions.filter((opt) => value?.includes(opt.value));
         }
         return mappedOptions.find((opt) => opt.value == value) ?? null;
      }, [value, mappedOptions, isMulti, allOptionValues.length, selectAllOption]);

      const bg = useColorModeValue('white', '#2d3748');
      const fontColor = useColorModeValue('#9E9E9E', '#ffffff');

      const handleMenuOpen = useCallback(() => {
         if (escPressedRef.current) return; // 🚀 BLOCK reopen
         setMenuIsOpen(true);
         setIsFocused(true);
      }, []);

      const handleMenuClose = useCallback(() => {
         setMenuIsOpen(false);
         setIsFocused(false);
      }, []);

      const handleBlur = useCallback(() => {
         setIsFocused(false);
         setMenuIsOpen(false);
      }, []);

      const CheckboxOption = useCallback((props) => {
         const isSelectAll = props.data.value === '*';
         return (
            <components.Option {...props}>
               <input type="checkbox" checked={props.isSelected} readOnly style={{ marginRight: 8 }} />
               {isSelectAll ? <strong>{props.label}</strong> : props.label}
            </components.Option>
         );
      }, []);

      const CustomOptionWithEdit = useCallback((props) => {
         const { data, innerRef, innerProps, isFocused, isSelected, selectProps } = props;

         const handleIconClick = (e) => {
            e.stopPropagation();
            selectProps.handleEdit(data);
         };

         return (
            <Box
               ref={innerRef}
               {...innerProps}
               bg={isSelected && !selectProps.isMulti ? '#3182ce' : isFocused ? '#ebf8ff' : 'white'}
               color={isSelected && !selectProps.isMulti ? 'white' : 'black'}
               px={3}
               py={2}
               cursor="pointer"
               display="flex"
               alignItems="center"
               justifyContent="space-between">
               <div>
                  <Text>{data.label}</Text>
                  {['salesdaybook'].includes(selectProps.openfrom) && data.bal !== 0 && (
                     <Text as="span" fontSize="sm" color={data.bal > 0 ? 'green.500' : 'red.500'}>
                        ( Balance: ₹{data.bal} )
                     </Text>
                  )}
                  {['item'].includes(selectProps.openfrom) && data.stock !== 0 && data?.stock && (
                     <Text as="span" fontSize="m" color={data.stock > 0 ? 'green.500' : 'red.500'}>
                        stock: {data.stock}
                     </Text>
                  )}
               </div>
               {!['city', 'state', 'country', 'ledger'].includes(selectProps.openfrom) && (
                  <Icon as={FiEdit} boxSize={4} color="gray.500" onClick={handleIconClick} cursor="pointer" />
               )}
            </Box>
         );
      }, []);

      const CustomValueContainer = useCallback(({ children, ...props }) => {
         const selected = props.getValue();

         if (props.selectProps.isMulti && selected.length > 1) {
            const label = selected[0].label;
            const extra = selected.length - 1;

            if (selected.length - 1 === props.selectProps.options.length - 1) {
               // accounting for select all option
               return (
                  <components.ValueContainer {...props}>
                     <div style={{ cursor: 'pointer', width: '100%' }}>Select All</div>
                  </components.ValueContainer>
               );
            }

            return (
               <components.ValueContainer {...props}>
                  <div style={{ cursor: 'pointer', width: '100%' }}>{`${label} + ${extra} more`}</div>
               </components.ValueContainer>
            );
         }

         return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
      }, []);
      useEffect(() => {
         const handleClickOutside = (event) => {
            // Get the menu portal element
            const menuPortal = document.querySelector('.react-select__menu-portal');

            // Check if we clicked outside both the control and the menu
            if (
               selectRef.current &&
               !selectRef.current.controlRef.contains(event.target) &&
               (!menuPortal || !menuPortal.contains(event.target))
            ) {
               setMenuIsOpen(false);
            }
         };

         document.addEventListener('mousedown', handleClickOutside);
         return () => {
            document.removeEventListener('mousedown', handleClickOutside);
         };
      }, []);
      const handleKeyDown = useCallback(
         (e) => {
            if (e.key === 'Escape') {
               setMenuIsOpen(false);
               selectRef.current?.blur();
               return;
            }

            if (!menuIsOpen) {
               onKeyDown?.(e);
               return;
            }

            if (e.key === 'Enter' || e.key === 'Tab') {
               const focusedOption = selectRef.current?.state.focusedOption;
               if (focusedOption) {
                  if (isMulti) {
                     const currentValue = value || [];
                     const newValue = currentValue.includes(focusedOption.value)
                        ? currentValue.filter((v) => v !== focusedOption.value)
                        : [...currentValue, focusedOption.value];
                     onChange(newValue);
                  } else {
                     onChange(focusedOption.value);
                     setMenuIsOpen(false);
                  }
               }
               e.preventDefault();
            }

            if (e.ctrlKey && e.key === 'Enter') {
               setIsOpen(true);
               setDemoname(inputValue);
            }

            onKeyDown?.(e);
            props?.nextRef?.current?.focus?.();
         },
         [menuIsOpen, isMulti, value, onChange, inputValue, onKeyDown, props?.nextRef, showCustomNoOptions],
      );

      const MenuList = useCallback(({ children, options, maxHeight, getValue }) => {
         const [value] = getValue();
         const initialOffset = options.indexOf(value) * OPTION_HEIGHT;

         if (!children.length) return null;

         return (
            <List
               height={Math.min(maxHeight, children.length * OPTION_HEIGHT)}
               itemCount={children.length}
               itemSize={OPTION_HEIGHT}
               initialScrollOffset={initialOffset}
               width="100%">
               {({ index, style }) => <div style={style}>{children[index]}</div>}
            </List>
         );
      }, []);

      const CustomNoOptionsMessage = useCallback(
         (props) => {
            return (
               <components.NoOptionsMessage {...props}>
                  <Box width="100%" textAlign="center">
                     <Box color="gray.500">No data found</Box>
                     <Button
                        mt={2}
                        size="xs"
                        colorScheme="blue"
                        onMouseDown={(e) => {
                           e.stopPropagation();
                           e.preventDefault();
                           props.selectProps.onAddNew?.(props.selectProps.inputValue);
                           setIsOpen(true);
                           setDemoname(props.selectProps.inputValue);
                        }}>
                        Add {label}
                     </Button>
                  </Box>
               </components.NoOptionsMessage>
            );
         },
         [label],
      );

      const handleChange = useCallback(
         (selected, actionMeta) => {
            if (isMulti) {
               // Multi-select logic
               if (!selected || selected.length === 0) {
                  const actualOptions = mappedOptions.filter((opt) => opt.value !== '*');
                  if (actualOptions.length > 0) {
                     onChange([]);
                  } else {
                     onChange([]);
                  }
                  return;
               }

               const isSelectAllClicked = selected.some((opt) => opt.value === '*');

               if (isSelectAllClicked && actionMeta.action === 'select-option') {
                  if (selected.length === mappedOptions.length) {
                     const actualOptions = mappedOptions.filter((opt) => opt.value !== '*');
                     if (actualOptions.length > 0) {
                        onChange([actualOptions[0].value]);
                     } else {
                        onChange([]);
                     }
                  } else {
                     const actualValues = mappedOptions.filter((opt) => opt.value !== '*').map((opt) => opt.value);
                     onChange(actualValues);
                  }
                  return;
               }

               if (actionMeta.action === 'deselect-option' && actionMeta.option.value === '*') {
                  const actualOptions = mappedOptions.filter((opt) => opt.value !== '*');
                  if (actualOptions.length > 0) {
                     onChange([actualOptions[0].value]);
                  } else {
                     onChange([]);
                  }
                  return;
               }

               // Normal deselection
               if (actionMeta.action === 'deselect-option') {
                  const values = selected.map((item) => item.value).filter((val) => val !== '*');

                  if (values.length === 0) {
                     const actualOptions = mappedOptions.filter((opt) => opt.value !== '*');
                     if (actualOptions.length > 0) {
                        onChange([actualOptions[0].value]);
                        return;
                     }
                  }
                  onChange(values);
                  return;
               }

               // Normal selection
               const values = selected.map((item) => item.value).filter((val) => val !== '*');
               onChange(values);
            } else {
               if (actionMeta.action === 'clear') {
                  const actualOptions = mappedOptions.filter((opt) => opt.value !== '*');
                  if (actualOptions.length > 0) {
                     onChange(actualOptions[0].value);
                  } else {
                     onChange('');
                  }
               } else if (actionMeta.action === 'deselect-option') {
                  const actualOptions = mappedOptions.filter((opt) => opt.value !== '*');
                  if (actualOptions.length > 0) {
                     onChange(actualOptions[0].value);
                  } else {
                     onChange('');
                  }
               } else if (selected) {
                  // Normal selection
                  onChange(selected.value ?? '');
               } else {
                  const actualOptions = mappedOptions.filter((opt) => opt.value !== '*');
                  if (actualOptions.length > 0) {
                     onChange(actualOptions[0].value);
                  } else {
                     onChange('');
                  }
               }
               setMenuIsOpen(false);
            }
         },
         [isMulti, onChange, mappedOptions, allOptionValues],
      );

      const handleSave = useCallback(
         (formData) => {
            onClose();
            const newOption = { ...formData, label: formData.name, value: formData.id };
            setAddoption(newOption.value);
            onChange(newOption.value);
            setMenuIsOpen(true);
            setIsFocused(true);
         },
         [onChange, onClose],
      );

      const handleClose = useCallback(() => {
         setIsOpen(false);
         setMenuIsOpen(true);
      }, []);

      useImperativeHandle(ref, () => ({
         focus: () => {
            selectRef.current?.focus();
            // ❌ don't open menu automatically
         },
      }));

      const bgcolor = useColorModeValue('#ffffff', '#101f4a');
      const valueColor = useColorModeValue('#000000', '#ffffff');

      const selectedbgColor = useColorModeValue('#d2e0f4ff', '#1A365D');
      const focusedbgColor = useColorModeValue('#d2e0f4ff', '#2D3748');
      const optionbgColor = useColorModeValue('#ffffff', '#1A202C');
      const optionTextColor = useColorModeValue('#000000', '#ffffff');

      const selectStyles = useMemo(
         () => ({
            control: (base, state) => ({
               ...base,
               borderRadius: '6px',
               width: '100%',
               borderColor: errors[errorName] ? 'red' : state.isFocused ? 'blue' : '#CBD5E0',
               boxShadow: errors[errorName] ? '0 0 0 1px #e53e3e' : state.isFocused ? '0 0 0 1px #3182CE' : 'none',
               backgroundColor: bg,
               color: fontcolor,
               '&:hover': { borderColor: '#A0AEC0' },
               minHeight: '40px',
            }),
            menu: (base) => ({
               ...base,
               zIndex: 9999,
               position: 'absolute',
               maxHeight: 300,
               backgroundColor: bgcolor,
            }),
            option: (base, { isFocused, isSelected }) => ({
               ...base,
               backgroundColor: isSelected && !isMulti ? selectedbgColor : isFocused ? focusedbgColor : optionbgColor,
               color: optionTextColor,
               cursor: 'pointer',
               padding: '8px 12px',
            }),
            menuPortal: (base) => ({
               ...base,
               zIndex: 9999,
            }),
            valueContainer: (base) => ({
               ...base,
               padding: '2px 8px',
            }),
            input: (base) => ({
               ...base,
               margin: 0,
               paddingBottom: 0,
               paddingTop: 0,
               color: valueColor,
            }),
            singleValue: (baseStyles) => ({
               ...baseStyles,
               color: valueColor,
            }),
         }),
         [bg, bgcolor, valueColor, errorName, errors, isMulti],
      );

      useEffect(() => {
         const handleEsc = (e) => {
            if (e.key === 'Escape') {
               escPressedRef.current = true;
               setMenuIsOpen(false);
               selectRef.current?.blur();

               // reset after event cycle
               setTimeout(() => {
                  escPressedRef.current = false;
               }, 100);
            }
         };

         window.addEventListener('keydown', handleEsc);
         return () => window.removeEventListener('keydown', handleEsc);
      }, []);

      return (
         <>
            <FormControl isRequired={isRequired} isInvalid={isInvalid} position="relative">
               <Box position="relative" zIndex={1}>
                  <Select
                     {...props}
                     openMenuOnFocus={false}
                     openMenuOnClick={true}
                     ref={selectRef}
                     id={name}
                     menuIsOpen={menuIsOpen}
                     onMenuOpen={handleMenuOpen}
                     onMenuClose={handleMenuClose}
                     onBlur={() => {
                        setIsFocused(false);
                        setMenuIsOpen(false);
                     }}
                     blurInputOnSelect={false}
                     onKeyDown={handleKeyDown}
                     value={selectedOption}
                     onChange={handleChange}
                     options={mappedOptions}
                     isMulti={isMulti}
                     placeholder=""
                     isClearable
                     menuPlacement="auto"
                     // MODIFIED: Trim the input value
                     onInputChange={(value, action) => {
                        // Only trim if user is typing, not on other actions
                        if (action.action === 'input-change') {
                           setInputValue(value.trimStart()); // Trim leading spaces while typing
                        } else {
                           setInputValue(value);
                        }
                     }}
                     inputValue={inputValue}
                     // ADD: Custom filter to trim search term
                     filterOption={(option, searchText) => {
                        const trimmedSearch = searchText.trim().toLowerCase();
                        const label = option.label?.toLowerCase() || '';
                        return label.includes(trimmedSearch);
                     }}
                     closeMenuOnSelect={!isMulti}
                     hideSelectedOptions={false}
                     menuPortalTarget={document.body}
                     maxMenuHeight={MENU_MAX_HEIGHT}
                     components={{
                        ...(isMulti
                           ? {
                              Option: CheckboxOption,
                              ValueContainer: CustomValueContainer,
                              ...(showCustomNoOptions && { NoOptionsMessage: CustomNoOptionsMessage }),
                           }
                           : {
                              MenuList,
                              ...(showCustomNoOptions && {
                                 Option: CustomOptionWithEdit,
                                 NoOptionsMessage: CustomNoOptionsMessage,
                              }),
                           }),
                     }}
                     styles={selectStyles}
                     handleEdit={(option) => {
                        setIsEditMode(true);
                        setCurrentRow(option);
                        setIsOpen(true);
                     }}
                  />

                  <FormLabel
                     htmlFor={name}
                     position="absolute"
                     top={isFloating ? '-11px' : '13px'} // Adjusted
                     left="14px" // Adjusted
                     fontSize={
                        isFloating ? { base: '11px', md: '12px', xl: '12px' } : { base: '14px', md: '16px', xl: '16px' }
                     }
                     color="gray.500"
                     backgroundColor={bg}
                     textColor={fontColor}
                     px="8px" // Increased padding
                     m={0} // Remove margin
                     zIndex={2}
                     lineHeight="1.2" // Fixed line height
                     transition="all 0.2s ease-in-out"
                     pointerEvents="none">
                     {label}
                  </FormLabel>
                  {errors[errorName ?? name] && <FormErrorMessage>{errors[errorName ?? name]}</FormErrorMessage>}
               </Box>
            </FormControl>
            {openfrom === 'item' && (
               <ItemMasterForm
                  isOpen={isOpen}
                  onClose={handleClose}
                  isEditMode={isEditMode}
                  demoname={demoname}
                  currentRow={currentRow}
                  onSave={handleSave}
                  saveandcontinue={false}
               />
            )}
            {openfrom === 'city' && (
               <>
                  <CityMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'state' && (
               <>
                  <StateMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'country' && (
               <>
                  <CountryMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'transport' && (
               <>
                  <TransportMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'bankBranch' && (
               <>
                  <BankAccountsForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'ledger' && (
               <>
                  <LedgerMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'salesRepresentative' && (
               <>
                  <SalesRepresentativeMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'brand' && (
               <>
                  <BrandMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'hsn' && (
               <>
                  <HsnMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'account' && (
               <>
                  <AddEditForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     initialData={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'itemgroups' && (
               <>
                  <ItemGroupMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'wareHouse' && (
               <>
                  <WarehouseMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'daybook' && (
               <>
                  <DaybookForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'salesdaybook' && (
               <>
                  <DaybookForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'series' && (
               <>
                  <SeriesMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'color' && (
               <>
                  <ColorMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'vehicle' && (
               <>
                  <VehicleMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'Saleman' && (
               <>
                  <AddUserModal
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     usertype={1}
                     companyList={companyList}
                     saveandcontinue={false}
                     username={demoname}
                  />
               </>
            )}
         </>
      );
   },
);

export const renderDynamicDropdown = (
   name,
   label,
   options,
   props,
   errors = {},
   textColor = 'gray.700',
   isRequired = false,
   placeholder = 'Select an option',
   isSearchable = true,
   filteredOptions,
   isMulti = false,
   onKeyDown,
) => {
   return (
      <DynamicDropdown
         name={name}
         label={label}
         options={options}
         openfrom={props.openfrom}
         value={props.value}
         showCustomNoOptions={props.showCustomNoOptions}
         errors={errors}
         errorName={props.errorName}
         textColor={textColor}
         onChange={props.onChange}
         selectRef={props.selectRef}
         isRequired={isRequired}
         placeholder={placeholder}
         isSearchable={isSearchable}
         filteredOptions={filteredOptions}
         onKeyDown={onKeyDown}
         {...props}
         isMulti={isMulti}
      />
   );
};
